import get from 'lodash-es/get';
import { BehaviorSubject, Observable, of as observableOf } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { AppointmentAnalyticsBaseService } from '@app/core/appointment-analytics-base.service';
import { UserService } from '@app/core/user.service';
import { Office } from '@app/shared/office';
import { Provider } from '@app/shared/provider';
import { ServiceArea } from '@app/shared/service-area';
import { Task } from '@app/shared/task';
import { SurveyData } from '@app/survey/survey-data';

import { FetchAppointmentBookingStateCache_patient_appointmentBookingSession_appointmentBookingStateCache_appointmentBookingState as CachedAppointmentBookingState } from '../__generated__/FetchAppointmentBookingStateCache';
import { AppointmentBookingSession } from '../appointment-booking-session';
import { AppointmentCancellationReason } from '../appointment-cancellation-reason';
import { AppointmentType } from '../appointment-type';
import { FromAppointment } from '../from-appointment';
import { AppointmentInventory } from '../provider-inventories';
import { ProviderType, ProviderTypeUtil } from '../provider-type';

export enum AppointmentBookingType {
  Book = 'Book',
  Reschedule = 'Reschedule',
  SwitchToRemote = 'SwitchToRemote',
}

interface TrackableBookingState {
  appointment_type?: string;
  appointment_type_id?: number;
  is_routed?: boolean;
  provider_category?: string;
  provider_id?: number;
  reason?: string;
  reason_for_cancel?: string;
  send_sms?: boolean;
  service_area?: string;
  routed_topic?: string;
  recommended_channel?: string;
  source?: string;
  flow?: string;
}

// Map appointment types to surveys
export const enum HardcodedAppointmentTypeId {
  IUDInsertion = 176,
  IUDRemoval = 177,
  IUDReplacement = 178,
}
export const enum HardcodedSurveySystemName {
  IUDInsertion = 17, // insertion survey is for replacement too
  IUDRemoval = 18,
}

export const PROVIDER_TYPE_DEFAULT = ProviderType.anyAvailableProvider;

function appointmentTypeIdToSurveySystemName(appointmentTypeId: number): HardcodedSurveySystemName | undefined {
  const appointmentSurveyMap = new Map([
    [HardcodedAppointmentTypeId.IUDInsertion, HardcodedSurveySystemName.IUDInsertion],
    [HardcodedAppointmentTypeId.IUDRemoval, HardcodedSurveySystemName.IUDRemoval],
    [HardcodedAppointmentTypeId.IUDReplacement, HardcodedSurveySystemName.IUDInsertion],
  ]);
  return appointmentSurveyMap.get(appointmentTypeId);
}

export class AppointmentBookingState {
  private _associatedTask: Task;
  private _isAppointmentRecommended = new BehaviorSubject<boolean>(false);
  private _selectedServiceArea$ = new BehaviorSubject<ServiceArea>(null);

  // This is a kind of hacky way to track if user is on appointment booking through a post-reg booking flow
  // TODO: figure out a better way to track appointment booking properties cross-flows
  private postRegistrationAppointment = false;

  appointmentType: AppointmentType;
  cancellationReason: AppointmentCancellationReason | undefined;
  createdPatientSurveyId: number;
  freeTextReason = '';
  fromAppointment: FromAppointment | undefined;
  isRecommendedRemoteAppointment = false;
  phoneNumber: string;
  recommendedChannel: string;
  requiredSurveySystemName: number;
  routedTopic: string;
  routingMetadataUuid: string;
  selectedProvider: Provider;
  selectedProviderType: ProviderType = ProviderType.anyAvailableProvider;
  sendSms: boolean;
  surveyData: SurveyData | undefined;
  source: string | undefined;
  flow: string | undefined;
  bookingType: AppointmentBookingType = AppointmentBookingType.Book;

  // attributes for "review booking" state
  selectedInventory: AppointmentInventory | undefined | null;
  inventoryProvider: Provider;
  office: Office;
  analyticsService: AppointmentAnalyticsBaseService;
  locationAttestation: string | undefined | null;
  afterHoursBillingDisclosure: boolean | undefined;

  isAppointmentRecommended$ = this._isAppointmentRecommended.asObservable();
  selectedServiceArea$: Observable<ServiceArea> = this._selectedServiceArea$
    .asObservable()
    .pipe(filter(sa => sa != null));

  get reason() {
    if (this.freeTextReason.length > 128) {
      return this.freeTextReason.trim().slice(0, 127) + '\u2026'; // ellipsis
    } else {
      return this.freeTextReason;
    }
  }

  set reason(reason) {
    this.freeTextReason = reason;
  }

  get associatedTask() {
    return this._associatedTask;
  }

  set associatedTask(task) {
    this._associatedTask = task;
  }

  get bookingParams() {
    const params: Record<string, any> = {
      appointment: {
        appointment_cancellation_reason_id: this.getCancellationReasonId(),
        appointment_type_id: this.getAppointmentTypeId(),
        from_appointment_id: this.getFromAppointmentId(),
        patient_survey_id: this.createdPatientSurveyId,
        reason: this.reason,
        routing_metadata_uuid: this.routingMetadataUuid,
        send_sms: this.sendSms,
        sms_number: this.sendSms ? this.phoneNumber : null,
        visit_reason_categories: null,
      },
    };

    if (this.associatedTask) {
      params.appointment.booked_from = {
        resource: this.associatedTask.type,
        id: this.associatedTask.id,
      };
    }

    return params;
  }

  get trackableProperties(): TrackableBookingState {
    const properties: TrackableBookingState = {
      appointment_type: this.appointmentType?.displayName,
      appointment_type_id: this.getAppointmentTypeId(),
      routed_topic: this.routedTopic,
      recommended_channel: this.recommendedChannel,
    };

    if (this.freeTextReason) {
      properties.reason = this.reason;
      properties.is_routed = this.getIsAppointmentRecommended();
    }

    const selectedServiceArea = this.getSelectedServiceArea();
    if (Boolean(selectedServiceArea)) {
      properties.service_area = selectedServiceArea.name;
    }

    if (this.selectedProviderType != null) {
      properties.provider_category = ProviderTypeUtil.getTypeDescription(this.selectedProviderType);
    }

    const provider = this.selectedProvider;
    if (Boolean(provider)) {
      properties.provider_id = provider.id;
      properties.provider_category = properties.provider_category || 'Specific Provider: ' + provider.preparedName;
    }

    if (this.sendSms) {
      properties.send_sms = true;
    }

    if (this.getSource()) {
      properties.source = this.getSource();
    }

    if (this.getCancellationReasonId()) {
      properties.reason_for_cancel = this.cancellationReason.displayReason;
    }

    if (this.flow) {
      properties.flow = this.flow;
    }

    return properties;
  }

  static fromAppointmentBookingSession(
    response: AppointmentBookingSession,
    userService: UserService,
  ): Observable<AppointmentBookingState> {
    const bookingState = new AppointmentBookingState();
    const { appointmentBookingState } = response.appointmentBookingStateCache;

    // return an empty booking state if the graphql response is empty
    if (!appointmentBookingState) {
      return observableOf(bookingState);
    }

    const appointmentReason = get(response, 'appointmentBookingStateCache.appointmentReason');
    if (!!appointmentReason) {
      bookingState.freeTextReason = appointmentReason;
    }

    // Use service area object to set selected service area;
    const serviceAreaSet$ = bookingState.getServiceAreaSet(appointmentBookingState, userService);

    return serviceAreaSet$.pipe(
      map((copyOfBookingState: AppointmentBookingState) =>
        copyOfBookingState.setAttributesFromAppointmentBookingSession(appointmentBookingState),
      ),
    );
  }

  getSource(): string {
    if (this.postRegistrationAppointment) {
      return 'Schedule Visit Page';
    } else if (this.isRecommendedRemoteAppointment) {
      return 'Next Available Remote Visit Widget';
    } else if (this.source) {
      return this.source;
    }
  }

  setAttributesFromAppointmentBookingSession(appointmentBookingSession: any) {
    if (appointmentBookingSession.appointmentType) {
      this.setSelectedAppointmentType(AppointmentType.fromGraphQL(appointmentBookingSession.appointmentType));
    }

    if (appointmentBookingSession.isAppointmentRecommended) {
      this.setIsAppointmentRecommended(appointmentBookingSession.isAppointmentRecommended);
    }

    if (appointmentBookingSession.fromAppointment) {
      this.setFromAppointment(FromAppointment.fromGraphQL(appointmentBookingSession.fromAppointment));
    }

    if (appointmentBookingSession.appointmentCancellationReason) {
      this.setCancellationReason(
        AppointmentCancellationReason.fromGraphQL(appointmentBookingSession.appointmentCancellationReason),
      );
    }

    // unset selected provider and set to anyAvailableProvider
    this.setSelectedProviderType(ProviderType.anyAvailableProvider);
    this.setSelectedProvider(null);

    return this;
  }

  getServiceAreaSet({ serviceArea }: CachedAppointmentBookingState, userService: UserService) {
    if (serviceArea) {
      this.setSelectedServiceArea(ServiceArea.fromAppointmentBookingSession(serviceArea));
      return observableOf(this);
    } else {
      return userService.user$.pipe(
        map(user => {
          this.setSelectedServiceArea(new ServiceArea(user.serviceArea));
          return this;
        }),
      );
    }
  }

  setAttributesForReviewBooking(
    selectedInventory: AppointmentInventory | undefined | null,
    inventoryProvider: Provider,
    office: Office,
    analyticsService: AppointmentAnalyticsBaseService,
    locationAttestation: string | undefined | null,
    afterHoursBillingDisclosure: boolean | undefined,
  ) {
    this.selectedInventory = selectedInventory;
    this.inventoryProvider = inventoryProvider;
    this.office = office;
    this.analyticsService = analyticsService;
    this.locationAttestation = locationAttestation;
    this.afterHoursBillingDisclosure = afterHoursBillingDisclosure;
  }

  setIsRecommendedRemoteAppointment(isRecommendedRemoteAppointment: boolean) {
    this.isRecommendedRemoteAppointment = isRecommendedRemoteAppointment;
  }

  setIsAppointmentRecommended(isAppointmentRecommended: boolean) {
    this._isAppointmentRecommended.next(isAppointmentRecommended);
  }

  getIsAppointmentRecommended() {
    return this._isAppointmentRecommended.value;
  }

  setSelectedServiceArea(serviceArea: ServiceArea) {
    this._selectedServiceArea$.next(serviceArea);
  }

  getSelectedServiceArea(): ServiceArea {
    return this._selectedServiceArea$.value;
  }

  setSelectedProviderType(providerType: ProviderType) {
    const ensureProviderType = Object.values(ProviderType).includes(providerType)
      ? providerType
      : PROVIDER_TYPE_DEFAULT;
    this.selectedProviderType = ensureProviderType;
  }

  setSelectedProvider(provider: Provider | null) {
    this.selectedProvider = provider;
  }

  getSelectedProviderId(): number {
    if (this.selectedProvider) {
      return this.selectedProvider.id;
    }
  }

  setSelectedAppointmentType(appointmentType: AppointmentType, resetSurvey = true) {
    if (appointmentType) {
      this.appointmentType = appointmentType;
    }
    if (resetSurvey) {
      this.resetSurvey();
    }
  }

  setCancellationReason(cancellationReason: AppointmentCancellationReason) {
    this.cancellationReason = cancellationReason;
  }

  getCancellationReasonId(): string {
    return this.cancellationReason ? this.cancellationReason.id : undefined;
  }

  setFromAppointment(fromAppointment: FromAppointment) {
    this.fromAppointment = fromAppointment;
  }

  getFromAppointmentId(): number {
    return this.fromAppointment ? this.fromAppointment.id : undefined;
  }

  resetSurvey() {
    this.requiredSurveySystemName = appointmentTypeIdToSurveySystemName(this.getAppointmentTypeId());
    this.createdPatientSurveyId = undefined;
    this.surveyData = undefined;
  }

  serviceAreaSet(): boolean {
    return this.getSelectedServiceArea() != null;
  }

  toApiV2() {
    const api_v2_data: Record<string, any> = {
      appointment_booking_state: {
        appointment_cancellation_reason: this.getCancellationReasonId() ? this.cancellationReason.toApiV2() : null,
        appointment_type: this.appointmentType ? this.appointmentType.toApiV2() : null,
        is_appointment_recommended: this.getIsAppointmentRecommended(),
        provider: this.selectedProvider != null ? this.selectedProvider.toApiV2() : null,
        provider_type: this.selectedProviderType,
        selected_reasons: [{ text: this.freeTextReason }], // backward compat from old problem typeahead which used selected_reasons
        service_area: this.getSelectedServiceArea().toApiV2(),
      },
      appointment_category: ProviderTypeUtil.getTypeCategory(this.selectedProviderType),
      appointment_reason: this.reason,
      appointment_type_id: this.getAppointmentTypeId(),
      provider_id: this.getSelectedProviderId(),
      service_area_id: this.getSelectedServiceArea().id,
      task: this.associatedTask != null ? this.associatedTask.id : null,
      visit_reason_categories: null,
    };

    if (this.fromAppointment) {
      api_v2_data.appointment_booking_state.from_appointment = this.fromAppointment.toApiV2();
    }
    return api_v2_data;
  }

  getAppointmentTypeId() {
    return this.appointmentType ? this.appointmentType.id : undefined;
  }

  updateRecommendedAppointmentType(recommendedAppointmentType?: AppointmentType, channel?: string, topic?: string) {
    if (!recommendedAppointmentType) {
      this.setSelectedAppointmentType(null);
      this.routedTopic = undefined;
      this.recommendedChannel = undefined;
      this.setIsAppointmentRecommended(false);
    } else {
      this.routedTopic = topic;
      this.recommendedChannel = channel;
      this.setSelectedAppointmentType(recommendedAppointmentType);
      this.setIsAppointmentRecommended(true);
    }
  }

  isBooking(): boolean {
    return this.bookingType === AppointmentBookingType.Book;
  }

  isRescheduling(): boolean {
    return this.bookingType === AppointmentBookingType.Reschedule;
  }

  isSwitchingToRemote(): boolean {
    return this.bookingType === AppointmentBookingType.SwitchToRemote;
  }
}
